import { Car, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import {
  type OwnerVehicleRow,
  formatZar,
  formatDate,
} from '../../utils/statements'
import { OperationalStateChip, LifecycleChip, ChipLabel } from './StateChips'

export function VehicleList({
  ownerName,
  vehicles,
  selectedVehicleId,
  onSelectVehicle,
}: {
  ownerName: string
  vehicles: OwnerVehicleRow[]
  selectedVehicleId: string | null
  onSelectVehicle: (vehicleId: string) => void
}) {
  return (
    <div className="flex min-h-0 w-full flex-col border-r border-border bg-background lg:w-96 lg:shrink-0">
      <div className="border-b border-border bg-card px-3 py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Vehicles
        </h2>
        <p className="mt-0.5 truncate text-sm font-medium text-foreground">{ownerName}</p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Car className="h-6 w-6" />
            No vehicles for this owner in the current filter.
          </div>
        ) : (
          <ul>
            {vehicles.map((v) => {
              const active = v.vehicle_id === selectedVehicleId
              return (
                <li key={v.vehicle_id}>
                  <button
                    type="button"
                    onClick={() => onSelectVehicle(v.vehicle_id)}
                    className={cn(
                      'flex w-full items-start gap-3 border-b border-border px-3 py-3 text-left transition-colors',
                      active ? 'bg-[hsl(var(--brand-soft))]' : 'hover:bg-accent',
                    )}
                  >
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold text-foreground">
                          {v.plate}
                        </span>
                        <span className="truncate text-sm font-medium text-foreground">
                          {v.vehicle_name || '—'}
                        </span>
                        {v.has_draft_statement && (
                          <span className="shrink-0 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                            Draft
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <ChipLabel>Lifecycle</ChipLabel>
                          <LifecycleChip status={v.lifecycle_status} />
                        </div>
                        <div className="flex items-center gap-1.5">
                          <ChipLabel>Operational</ChipLabel>
                          <OperationalStateChip state={v.operational_state} />
                        </div>
                      </div>
                      {v.operational_state_as_of && (
                        <p className="text-[11px] text-muted-foreground">
                          as of {formatDate(v.operational_state_as_of)}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5 text-xs">
                        <span className="text-muted-foreground">
                          Statements{' '}
                          <span className="font-medium tabular-nums text-foreground">
                            {v.statement_count}
                          </span>
                        </span>
                        <span className="text-muted-foreground">
                          Retention{' '}
                          <span className="font-medium tabular-nums text-foreground">
                            {formatZar(v.latest_retention_balance_zar)}
                          </span>
                        </span>
                        <span className="text-muted-foreground">
                          Closing{' '}
                          <span className="font-medium tabular-nums text-foreground">
                            {formatZar(v.latest_closing_balance_zar)}
                          </span>
                        </span>
                      </div>
                    </div>
                    <ChevronRight
                      className={cn(
                        'mt-1 h-4 w-4 shrink-0',
                        active ? 'text-[hsl(var(--brand))]' : 'text-muted-foreground',
                      )}
                    />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
